
import React, { useRef, useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { t } from '../locales';
import { Language } from '../types';
import { X, Upload, Camera, AlertCircle, RefreshCw, SwitchCamera } from 'lucide-react';

interface CameraCaptureModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCapture: (imageSrc: string) => void;
  language: Language;
}

const CameraCaptureModal: React.FC<CameraCaptureModalProps> = ({ isOpen, onClose, onCapture, language }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<string>('');
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setIsCameraReady(false);
  }, [stream]);

  const startCamera = useCallback(async () => {
    setError('');
    setIsCameraReady(false);
    
    // Ensure previous stream is stopped before starting new one
    if (stream) {
        stream.getTracks().forEach(track => track.stop());
    }

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError("Camera API not available. Please use the upload option.");
        return;
    }

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
          video: { 
              facingMode: facingMode,
              width: { ideal: 1920 },
              height: { ideal: 1080 }
          },
          audio: false 
      });

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        // Explicitly play and handle the promise
        try {
            await videoRef.current.play();
            setStream(mediaStream);
        } catch (playError) {
            console.error("Video play failed", playError);
            setError("Could not start video playback.");
        }
      }
    } catch (err: any) {
      console.error("Camera access error", err);
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          setError("Permission denied. Please enable camera access.");
      } else {
          setError("Could not connect to camera. Please upload a photo instead.");
      }
    }
  }, [facingMode]);

  useEffect(() => {
    if (isOpen) {
        startCamera();
    }
    return () => stopCamera();
  }, [isOpen, startCamera]);

  const toggleCamera = () => {
      setFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
  };

  const handleCapture = () => {
    if (videoRef.current && canvasRef.current && isCameraReady) {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        
        // Ensure canvas matches video aspect ratio
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        
        const ctx = canvas.getContext('2d');
        if (ctx) {
            // Mirror image if using front camera
            if (facingMode === 'user') {
                ctx.translate(canvas.width, 0);
                ctx.scale(-1, 1);
            }
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const imageSrc = canvas.toDataURL('image/jpeg', 0.85);
            onCapture(imageSrc);
            stopCamera();
            onClose();
        }
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
          const reader = new FileReader();
          reader.onloadend = () => {
              onCapture(reader.result as string);
              stopCamera();
              onClose();
          };
          reader.readAsDataURL(file);
      }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[80] bg-black flex flex-col animate-fadeIn">
        {/* Top bar with close */}
        <div className="absolute top-0 left-0 right-0 p-4 flex justify-between z-20 pointer-events-none">
            <button 
                onClick={toggleCamera}
                className="p-2 bg-black/40 rounded-full text-white pointer-events-auto backdrop-blur-md border border-white/10"
                title="Switch Camera"
            >
                <SwitchCamera size={24} />
            </button>
            <button 
                onClick={() => { stopCamera(); onClose(); }}
                className="p-2 bg-black/40 rounded-full text-white pointer-events-auto backdrop-blur-md border border-white/10"
            >
                <X size={24} />
            </button>
        </div>
        
        <div className="flex-1 relative flex items-center justify-center overflow-hidden">
            {error ? (
                <div className="text-white text-center p-8 max-w-sm flex flex-col items-center gap-6 animate-popIn">
                    <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center text-red-500">
                        <AlertCircle size={32} />
                    </div>
                    <p className="text-sm font-medium opacity-90">{error}</p>
                    
                    <div className="flex flex-col w-full gap-3">
                        <button 
                            onClick={() => fileInputRef.current?.click()} 
                            className="flex items-center justify-center gap-2 px-6 py-4 bg-white text-black font-bold rounded-2xl w-full shadow-xl active:scale-95 transition-transform"
                        >
                            <Upload size={20} />
                            Upload Photo
                        </button>
                        <button 
                            onClick={startCamera}
                            className="flex items-center justify-center gap-2 px-6 py-4 bg-white/10 text-white font-bold rounded-2xl w-full border border-white/20 active:scale-95 transition-transform"
                        >
                            <RefreshCw size={20} />
                            Try Again
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    <video 
                        ref={videoRef} 
                        autoPlay 
                        playsInline 
                        muted 
                        onLoadedMetadata={() => setIsCameraReady(true)}
                        className={`w-full h-full object-cover transition-opacity duration-500 ${isCameraReady ? 'opacity-100' : 'opacity-0'} ${facingMode === 'user' ? 'scale-x-[-1]' : ''}`} 
                    />
                    {!isCameraReady && (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-10 h-10 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
                        </div>
                    )}
                    
                    {/* Viewfinder Overlay (Matches Barcode Scanner Style) */}
                    <div className="absolute inset-0 pointer-events-none flex flex-col items-center justify-center">
                        <div className="w-[80vw] h-[50vh] border-2 border-white/30 rounded-3xl relative">
                            <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-white -mt-1 -ml-1 rounded-tl-xl"></div>
                            <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-white -mt-1 -mr-1 rounded-tr-xl"></div>
                            <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-white -mb-1 -ml-1 rounded-bl-xl"></div>
                            <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-white -mb-1 -mr-1 rounded-br-xl"></div>
                        </div>
                        <p className="mt-8 text-white font-bold bg-black/40 px-6 py-2 rounded-full text-xs uppercase tracking-widest backdrop-blur-md border border-white/10">
                            Align Recipe Document
                        </p>
                    </div>
                </>
            )}
            <canvas ref={canvasRef} className="hidden" />
            <input 
                type="file" 
                accept="image/*" 
                ref={fileInputRef} 
                className="hidden"
                onChange={handleFileUpload}
            />
        </div>

        {!error && (
            <div className="p-10 bg-black flex justify-between items-center pb-safe">
                 {/* Upload Fallback */}
                 <button 
                    onClick={() => fileInputRef.current?.click()}
                    className="w-12 h-12 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors"
                    title="Upload File"
                 >
                    <Upload size={20} />
                 </button>

                {/* Shutter Button */}
                <button 
                    onClick={handleCapture} 
                    disabled={!isCameraReady}
                    className={`w-20 h-20 rounded-full bg-white border-8 border-white/20 shadow-2xl flex items-center justify-center active:scale-90 transition-all ${!isCameraReady ? 'opacity-50 grayscale' : 'hover:scale-105'}`}
                >
                    <div className="w-14 h-14 rounded-full border-4 border-black"></div>
                </button>

                <div className="w-12"></div> {/* Spacer for symmetry */}
            </div>
        )}
    </div>,
    document.body
  );
};

export default CameraCaptureModal;
